<template>
	<div>
		<!-- LSP-16994: invisible 1x1 iframe of the contact-async app. Loading it resumes any pending
		     recording/photo/file uploads buffered in the async origin's IndexedDB on this device, so
		     mobile users can keep browsing admin while their voicemail/async uploads finish. The frame
		     stays mounted for the whole session (QA rework): uploads that start AFTER admin loads (e.g.
		     the user records from a link in another tab, then returns here) must still resume and
		     surface in this tab, and a permanent teardown made the frame untestable/undiscoverable.
		     LSP-20768: the `name` is load-bearing. On a back/forward or tab-restore load, WebKit hands each
		     script-inserted iframe the URL it had last visit, matching UNNAMED frames by creation order —
		     so when another script's iframe is created in a different order, this frame gets that script's
		     URL and the other frame gets /background-upload. A named frame is matched by its name. -->
		<iframe
			v-if="frameActive"
			ref="frameElement"
			:src="iframeSrc"
			:name="FRAME_NAME"
			class="async-background-upload-frame"
			title="Background upload"
			aria-hidden="true"
			tabindex="-1"
			data-test-id="async-background-upload-frame"
			@load="onFrameLoad"
			@error="onFrameError"></iframe>

		<v-snackbar
			v-model="snackbarVisible"
			:timeout="snackbarTimeout"
			color="primary"
			location="bottom"
			data-test-id="async-background-upload-snackbar">
			<div class="d-flex flex-column flex-sm-row align-sm-center ga-2">
				<div class="text-break">{{ snackbarMessage }}</div>
				<div class="d-flex align-center justify-end flex-shrink-0">
					<v-btn
						v-if="hasPendingPhotos"
						variant="text"
						slim
						data-test-id="async-background-upload-photo-recovery"
						@click="openPhotoRecovery">
						Finish photos
					</v-btn>
					<!-- F-17: photos had a button and everything else had a sentence telling the admin to go and
					     reopen a link. The iframe is already draining the recording/file lanes, so the honest
					     action here is "start the next attempt now" rather than sending them elsewhere. -->
					<v-btn
						v-if="showKeepTryingAction"
						variant="text"
						slim
						:disabled="keepTryingArmed"
						data-test-id="async-background-upload-keep-trying"
						@click="keepTryingNow">
						{{ keepTryingArmed ? "Trying…" : "Keep trying now" }}
					</v-btn>
					<v-btn
						icon="icon-close-medium"
						variant="plain"
						slim
						data-test-id="async-background-upload-snackbar-close"
						@click="dismissSnackbar"></v-btn>
				</div>
			</div>
		</v-snackbar>
	</div>
</template>

<script setup lang="ts">
	import { computed, onBeforeUnmount, onMounted, ref } from "vue";
	import DeviceUtility from "@/helpers/device";
	import {
		createDrainHostHello,
		createDrainHostWake,
		DRAIN_HOST_READY_TYPE,
		DRAIN_HOST_STATUS_TYPE,
		newUploadId,
		validateDrainHostEvent,
		validateDrainHostReadyEvent,
		type DrainHostStatusMessage,
		type DrainHostValidationState
	} from "@uploads";

	// If the frame never reports (bad deploy, blocked frame, storage denied), log it so the failure
	// is triagable instead of silently leaving pending uploads stranded.
	const FRAME_REPORT_WATCHDOG_MS = 30000;
	// LSP-20768: must stay constant across page loads — session-history restoration matches on it.
	const FRAME_NAME = "liveswitch-async-background-upload";

	const asyncDomain = computed(() => {
		const domain = window.env?.VITE_ASYNC_DOMAIN;
		if (!domain) {
			console.warn("[AsyncBackgroundUploader] VITE_ASYNC_DOMAIN is not configured; background uploads will not resume");
			return null;
		}
		return domain.replace(/\/+$/, "");
	});

	const asyncOrigin = computed(() => {
		if (!asyncDomain.value) return null;
		try {
			return new URL(asyncDomain.value).origin;
		} catch {
			console.warn(`[AsyncBackgroundUploader] VITE_ASYNC_DOMAIN is not a valid URL: ${asyncDomain.value}`);
			return null;
		}
	});

	// Touch devices only (UA-based, not viewport width) per LSP-16994: pending async uploads live in
	// the IndexedDB of the phone/tablet that recorded them; desktop admin sessions don't need the
	// frame, and mounting a second SPA there would double RUM sessions for near-zero recoveries.
	const isMobileDevice = DeviceUtility.isMobileOrTablet;

	const frameActive = computed(() => isMobileDevice && asyncOrigin.value !== null);
	const iframeSrc = computed(() => `${asyncDomain.value}/background-upload`);
	const frameElement = ref<HTMLIFrameElement | null>(null);
	const handshakeNonce = newUploadId();
	const validationState: DrainHostValidationState = { lastSequence: 0 };
	let frameSource: Window | null = null;

	const hadPending = ref(false);
	const snackbarVisible = ref(false);
	const snackbarMessage = ref("");
	const snackbarTimeout = ref(-1);
	const hostUnavailable = ref(false);
	const hasPendingPhotos = ref(false);
	// F-17: the non-photo needs-attention case. Photos get "Finish photos" (a page that keeps their
	// lane alive); everything else used to get a sentence and no control at all.
	const showKeepTryingAction = ref(false);
	// Debounces repeated presses and gives the button something honest to say while the child works.
	const keepTryingArmed = ref(false);
	let keepTryingResetTimer: ReturnType<typeof setTimeout> | null = null;
	// How long the button stays in its "Trying…" state. Long enough that a press reads as having done
	// something, short enough that a stuck child does not leave the action permanently disabled.
	const KEEP_TRYING_COOLDOWN_MS = 8000;
	// Closing the persistent "uploading" snackbar dismisses it for the rest of the session — uploads
	// keep going in the background and the brief "All done" confirmation still shows at the end.
	const uploadingSnackbarDismissed = ref(false);
	// Which message the snackbar is currently showing, so dismissal semantics stay per-category:
	// closing the benign progress nag must not silence a later needs-attention alert, and closing
	// the attention alert must not block later benign progress updates.
	let snackbarKind: "uploading" | "attention" | "done" | null = null;

	function dismissSnackbar(): void {
		snackbarVisible.value = false;
		if (snackbarKind === "uploading") uploadingSnackbarDismissed.value = true;
	}

	function openPhotoRecovery(): void {
		// LSP-15319: every fail condition on this path used to be silent, and this is the recovery
		// control for an ALREADY stuck photo lane — so a dead button here fails at the worst
		// possible moment, leaving the snackbar to keep nagging with no way forward.
		if (!asyncDomain.value) {
			console.warn("AsyncBackgroundUploader.openPhotoRecovery: no async domain configured — cannot open the recovery page", {
				hasPendingPhotos: hasPendingPhotos.value,
				hostUnavailable: hostUnavailable.value
			});
			showPopupBlockedSnackbar();
			return;
		}

		// Pre-open a blank tab rather than passing "noopener": with that feature set, window.open
		// returns null on SUCCESS as well as on failure (HTML spec — the whole point is to withhold
		// the handle), so its return value cannot distinguish the two and a null-check there would
		// toast on every working click. Opening blank yields a real handle, so a blocked popup or an
		// exhausted tab limit (iOS Safari caps around 500 — not distinguishable from blocking, and
		// identical in effect) is detectable, and clearing `opener` keeps the isolation "noopener"
		// was providing. Same pre-open-then-navigate shape as EntityActions.recordMore() and
		// openInternalRoute(). The target is our own async origin, so no referrer concern arises.
		const recoveryWindow = window.open("", "_blank");
		if (!recoveryWindow) {
			console.warn("AsyncBackgroundUploader.openPhotoRecovery: window.open returned null — popup blocked or tab limit reached", {
				hasPendingPhotos: hasPendingPhotos.value,
				asyncDomain: asyncDomain.value
			});
			showPopupBlockedSnackbar();
			return;
		}

		recoveryWindow.opener = null;
		recoveryWindow.location.href = `${asyncDomain.value}/background-upload?recovery=photos`;
	}

	function showPopupBlockedSnackbar(): void {
		// Reuse this component's own snackbar rather than introducing a toast it does not otherwise
		// use — the user is already looking at it, since the button that failed lives inside it.
		snackbarKind = "attention";
		snackbarMessage.value = "Unable to open a new window. Please check your popup blocker settings or close some tabs and try again.";
		snackbarTimeout.value = -1;
		snackbarVisible.value = true;
	}

	/**
	 * F-17: name what is actually stuck, from the child's own per-kind counts, instead of the generic
	 * "reopen the relevant upload or recording link". "The relevant link" was the part the admin could
	 * not resolve — they had no idea which media the alert was about.
	 */
	function describeStuckKinds(byKind: Readonly<Record<string, number>>): string {
		const labels: Record<string, string> = {
			photos: "photos",
			recordings: "recordings",
			files: "files",
			spark: "reports"
		};
		const stuck = Object.entries(byKind)
			.filter(([, count]) => count > 0)
			.map(([kind]) => labels[kind] ?? kind);
		if (stuck.length === 0) return "Some uploads";
		if (stuck.length === 1) return `Your ${stuck[0]}`;
		return `Your ${stuck.slice(0, -1).join(", ")} and ${stuck[stuck.length - 1]}`;
	}

	/**
	 * F-17: ask the embedded async app to start its next drain pass immediately.
	 *
	 * This is an ACCELERATOR, not the recovery itself — under R-0 those uploads are already on an
	 * automatic retry-forever schedule, and this only moves the next attempt to now. Every fail branch
	 * is logged with what was known at the time, because a button that silently does nothing is exactly
	 * the "attention needed with no action" shape this work exists to remove.
	 */
	function keepTryingNow(): void {
		if (keepTryingArmed.value) return;
		if (!frameActive.value || !asyncOrigin.value) {
			console.warn(
				`[AsyncBackgroundUploader] keep-trying ignored: frameActive=${frameActive.value}, asyncOrigin=${asyncOrigin.value ?? "unresolved"}`
			);
			return;
		}
		if (!frameSource) {
			console.warn("[AsyncBackgroundUploader] keep-trying ignored: the background upload frame has not handshaken yet");
			return;
		}
		try {
			frameSource.postMessage(createDrainHostWake(handshakeNonce), asyncOrigin.value);
			console.info("[AsyncBackgroundUploader] keep-trying: drain wake posted to the background upload frame");
		} catch (error) {
			console.warn("[AsyncBackgroundUploader] keep-trying could not reach the background upload frame", error);
			return;
		}
		keepTryingArmed.value = true;
		if (keepTryingResetTimer) clearTimeout(keepTryingResetTimer);
		keepTryingResetTimer = setTimeout(() => {
			keepTryingResetTimer = null;
			keepTryingArmed.value = false;
		}, KEEP_TRYING_COOLDOWN_MS);
	}

	/**
	 * LSP-19663: post the hello that carries this session's nonce.
	 *
	 * Called from the iframe's load event AND from the child's own `ready` announcement. The
	 * announcement is the reliable one: the child registers its message listener only after admission
	 * (runtime config fetch + IndexedDB schema + persistence), which in a production bundle finishes
	 * AFTER the frame's load event — and postMessage neither queues nor retries, so a hello posted on
	 * that event alone is simply lost. The load-event call stays as the compatibility path for a child
	 * deployment that predates the announcement; the child ignores a repeat hello with the same nonce.
	 *
	 * Every fail condition logs: a handshake that never completes is invisible otherwise, and the only
	 * symptom the user gets is a permanent "unavailable" alert 30 seconds later.
	 */
	function postHello(reason: string, readySource?: MessageEventSource | null): void {
		if (!frameActive.value || !asyncOrigin.value) {
			console.warn(
				`[AsyncBackgroundUploader] handshake hello skipped (${reason}): frameActive=${frameActive.value}, asyncOrigin=${asyncOrigin.value ?? "unresolved"}`
			);
			return;
		}
		const source = (readySource as Window | null | undefined) ?? frameElement.value?.contentWindow;
		if (!source) {
			console.warn(`[AsyncBackgroundUploader] handshake hello skipped (${reason}): the frame has no contentWindow`);
			return;
		}
		frameSource = source;
		// Unconditional, and it must stay that way: an iframe's WindowProxy is per browsing context,
		// so `contentWindow` identity is IDENTICAL after the child reloads (verified in Chromium and
		// WebKit). Any "same window, so skip the reset" shortcut therefore leaves lastSequence at N
		// while a reloaded child restarts at sequence 1, and the parent rejects its every status as a
		// replay — the snackbar silently stops updating. Cursor Bugbot caught exactly that on PR #4897.
		validationState.lastSequence = 0;
		frameHasReported = false;
		try {
			source.postMessage(createDrainHostHello(handshakeNonce), asyncOrigin.value);
			console.info(`[AsyncBackgroundUploader] handshake hello posted (${reason}) to ${asyncOrigin.value}`);
		} catch (error) {
			console.warn("[AsyncBackgroundUploader] Could not establish the background upload host handshake", error);
		}
	}

	/**
	 * LSP-20768: the iframe's load event. The async child is cross-origin, so `contentDocument` is null
	 * for it — a readable one means this frame is showing some other document (on WebKit, typically
	 * another frame's URL restored from session history). Logged because the only other symptom is the
	 * watchdog 30 seconds later, and the data needed to tell "slow" from "wrong document" is gone by then.
	 * `contentDocument` rather than `contentWindow.location`: the latter throws cross-origin, and WebKit
	 * prints a "Blocked a frame" console error for it even when the throw is caught.
	 */
	function onFrameLoad(): void {
		frameLoadCount++;
		const sameOriginHref = frameElement.value?.contentDocument?.URL ?? null;
		if (sameOriginHref && sameOriginHref !== "about:blank") {
			console.warn(
				`[AsyncBackgroundUploader] frame loaded ${sameOriginHref}, not the async origin ${asyncOrigin.value ?? "unresolved"} (navigationType=${navigationType()})`
			);
		}
		postHello("frame-load");
	}

	/**
	 * LSP-19663: the child's "my listener exists" announcement, and the parent's whole reason for not
	 * having to guess. Returns true when the event was an announcement, so the caller stops dispatching.
	 */
	function onReadyMessage(event: MessageEvent): boolean {
		const looksLikeReady =
			typeof event.data === "object" && event.data !== null && (event.data as { type?: unknown }).type === DRAIN_HOST_READY_TYPE;
		if (!looksLikeReady) return false;
		const result = validateDrainHostReadyEvent(event, {
			origin: asyncOrigin.value!,
			source: frameElement.value?.contentWindow ?? null
		});
		if (result.kind !== "accepted") {
			rejectedAnnouncements.push(result.kind);
			console.warn("[AsyncBackgroundUploader] handshake announcement rejected", {
				reason: result.kind,
				expectedOrigin: asyncOrigin.value,
				eventOrigin: event.origin,
				sourceIsFrame: event.source === frameElement.value?.contentWindow,
				// LSP-20768: an async-origin announcement from a window that is not this frame means another
				// iframe in this document is running /background-upload. Both of these name that case.
				iframeCount: document.querySelectorAll("iframe").length,
				navigationType: navigationType()
			});
			return true;
		}
		postHello("frame-ready", event.source);
		return true;
	}

	/**
	 * Is this event even claiming to be a drain-host status? Every log below is gated on it, so
	 * unrelated postMessage traffic on this window (dev tooling, other embeds, third-party frames)
	 * cannot produce a warning per message. Same shape as the child's `looksLikeWake` gate.
	 */
	function looksLikeStatus(event: MessageEvent): boolean {
		return typeof event.data === "object" && event.data !== null && (event.data as { type?: unknown }).type === DRAIN_HOST_STATUS_TYPE;
	}

	function warnStatusDropped(condition: string, event: MessageEvent): void {
		if (!looksLikeStatus(event)) return;
		console.warn(`[AsyncBackgroundUploader] status dropped (${condition})`, {
			expectedOrigin: asyncOrigin.value,
			eventOrigin: event.origin,
			sourceIsFrame: event.source === frameElement.value?.contentWindow
		});
	}

	function onMessage(event: MessageEvent): void {
		if (!frameActive.value) {
			warnStatusDropped("the background upload frame is not active", event);
			return;
		}
		if (!asyncOrigin.value) {
			warnStatusDropped("no async origin is configured", event);
			return;
		}
		// The announcement arrives BEFORE any hello, so it cannot be gated on an established frameSource.
		if (onReadyMessage(event)) return;
		if (!frameSource) {
			// The exact runtime signature of "the handshake never completed": a child is talking, but
			// this parent has never posted a hello, so it has no nonce to validate against.
			warnStatusDropped("no handshake yet — no hello has been posted to the frame", event);
			return;
		}
		const validation = validateDrainHostEvent(
			event,
			{ origin: asyncOrigin.value, source: frameSource, nonce: handshakeNonce },
			validationState
		);
		if (validation.kind !== "accepted") {
			warnStatusDropped(`rejected by the trust boundary: ${validation.kind}`, event);
			return;
		}
		const data: DrainHostStatusMessage = validation.status;
		hasPendingPhotos.value = (data.byKind.photos ?? 0) > 0;

		if (!frameHasReported) {
			// Diagnostic: pairs with the "did not report" watchdog so a working message channel is
			// visible in logs, not just a broken one.
			console.info(
				`[AsyncBackgroundUploader] frame reported: pendingTotal=${data.pendingTotal}, needsAttention=${data.needsAttention}`
			);
		}
		frameHasReported = true;
		frameEverReported = true;
		hostUnavailable.value = data.state === "unavailable";
		if (hostUnavailable.value) {
			// Nothing to accelerate: the child cannot drain at all.
			showKeepTryingAction.value = false;
			snackbarKind = "attention";
			snackbarMessage.value = "Background uploads are unavailable on this device right now. Keep this page open and try again later.";
			snackbarTimeout.value = -1;
			snackbarVisible.value = true;
			return;
		}

		if (data.pendingTotal > 0) {
			hadPending.value = true;
			const hasOnlyPendingPhotos = hasPendingPhotos.value && data.byKind.photos === data.pendingTotal;
			// A dismissal only silences the benign progress message — a needs-attention alert is a
			// different category (uploads are stuck and require user action), so it re-opens regardless.
			if (uploadingSnackbarDismissed.value && !data.needsAttention) {
				// Nothing is stuck any more, so the accelerator has nothing to accelerate.
				showKeepTryingAction.value = false;
				// The benign nag stays dismissed, but if the attention alert is still on screen the
				// stuck state has recovered — hide it rather than leaving stale "need attention" copy up.
				if (snackbarKind === "attention") {
					snackbarVisible.value = false;
					snackbarKind = null;
				}
				return;
			}
			// F-17: the non-photo needs-attention branch is the one that had copy but no control. Offer
			// the immediate-retry accelerator there — the iframe already drains the recording/file lanes,
			// so "try again now" is something this page can genuinely do, unlike "go and reopen a link".
			showKeepTryingAction.value = data.needsAttention && !hasOnlyPendingPhotos;
			snackbarKind = data.needsAttention ? "attention" : "uploading";
			snackbarMessage.value = data.needsAttention
				? hasOnlyPendingPhotos
					? "Some photos need attention. Open the photo recovery page to finish them."
					: // Names WHAT is stuck from the child's own per-kind counts. "The relevant upload or
						// recording link" was the part the admin could not resolve.
						`${describeStuckKinds(data.byKind)} haven't finished uploading yet. Keep this page open — we'll keep trying, or start another attempt now.`
				: hasPendingPhotos.value
					? "Your photos are still uploading. Open the photo recovery page to keep their upload active."
					: "We're finishing your pending uploads in the background — feel free to keep browsing.";
			snackbarTimeout.value = -1;
			snackbarVisible.value = true;
			return;
		}

		if (hadPending.value) {
			hasPendingPhotos.value = false;
			showKeepTryingAction.value = false;
			snackbarKind = "done";
			snackbarMessage.value = "All done! Your pending uploads have finished.";
			snackbarTimeout.value = 5000;
			snackbarVisible.value = true;
			// The frame stays mounted, so a NEW batch of uploads can start later in this session.
			// Reset the per-batch state: the next batch gets a fresh progress nag even if the user
			// dismissed the previous one.
			hadPending.value = false;
			uploadingSnackbarDismissed.value = false;
		} else if (snackbarKind !== "done") {
			snackbarVisible.value = false;
		}
	}

	let frameHasReported = false;
	// Separate from frameHasReported, which postHello() clears on every handshake so a genuinely
	// reloaded child restarts its sequence cleanly. postHello() runs from BOTH the iframe @load
	// handler and the child's ready announcement, and the child ignores a repeat hello carrying the
	// same nonce and will not re-post an unchanged status — so the second call could blank
	// frameHasReported with no reply coming, and the watchdog would raise a false, non-dismissable
	// "unavailable" alert after a perfectly good first report. This latch records that the frame has EVER reported
	// and is never cleared, so the watchdog still fires when the frame truly never answers.
	let frameEverReported = false;
	let watchdogTimer: ReturnType<typeof setTimeout> | null = null;
	// LSP-20768 diagnostics for the watchdog line: a frame that loaded late (loadEvents=1, nothing
	// rejected) and a frame whose announcement came from another window (rejected source-mismatch) end
	// in the same alert, and only these tell them apart.
	let frameLoadCount = 0;
	const rejectedAnnouncements: string[] = [];

	function navigationType(): string {
		try {
			return (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type ?? "unknown";
		} catch {
			return "unknown";
		}
	}

	function onFrameError(): void {
		console.warn("[AsyncBackgroundUploader] Background upload frame failed to load; pending uploads will not resume from admin");
		hostUnavailable.value = true;
		// There is no reachable child to wake, so never offer the accelerator here.
		showKeepTryingAction.value = false;
		snackbarKind = "attention";
		snackbarMessage.value = "Background uploads are unavailable on this device right now. Keep this page open and try again later.";
		snackbarTimeout.value = -1;
		snackbarVisible.value = true;
	}

	onMounted(() => {
		window.addEventListener("message", onMessage);
		if (!frameActive.value) {
			// Diagnostic: without this, an inactive frame is indistinguishable from a broken one.
			console.info(
				`[AsyncBackgroundUploader] inactive: mobileDevice=${isMobileDevice}, asyncOrigin=${asyncOrigin.value ?? "unresolved"}`
			);
		}
		if (frameActive.value) {
			watchdogTimer = setTimeout(() => {
				if (!frameEverReported && frameActive.value) {
					console.warn(
						`[AsyncBackgroundUploader] Background upload frame did not report within ${FRAME_REPORT_WATCHDOG_MS / 1000}s (origin: ${asyncOrigin.value}, helloPosted=${frameSource !== null}, loadEvents=${frameLoadCount}, rejectedAnnouncements=${rejectedAnnouncements.join("|") || "none"}, navigationType=${navigationType()}, visibility=${document.visibilityState})`
					);
					hostUnavailable.value = true;
					showKeepTryingAction.value = false;
					snackbarKind = "attention";
					snackbarMessage.value =
						"Background uploads are unavailable on this device right now. Keep this page open and try again later.";
					snackbarTimeout.value = -1;
					snackbarVisible.value = true;
				}
			}, FRAME_REPORT_WATCHDOG_MS);
		}
		// LSP-19663: no hello is posted from here. At mount (and at the following tick) the iframe has
		// not navigated yet, so contentWindow is still the initial about:blank carrying the ADMIN
		// origin — the browser dropped every such message on origin mismatch, once per mobile page
		// load. The handshake now starts from the frame's own load event and, decisively, from the
		// child's `ready` announcement.
	});
	onBeforeUnmount(() => {
		window.removeEventListener("message", onMessage);
		if (watchdogTimer) clearTimeout(watchdogTimer);
		if (keepTryingResetTimer) clearTimeout(keepTryingResetTimer);
	});
</script>

<style scoped>
	.async-background-upload-frame {
		position: fixed;
		bottom: 0;
		right: 0;
		width: 1px;
		height: 1px;
		border: 0;
		opacity: 0;
		pointer-events: none;
	}
</style>
